<script lang="ts" setup>
import type { SectionWithIndex } from '~/types'

interface Props extends Pick<SectionWithIndex, 'collection' | 'collectionId' | 'id' | 'index' | 'images' | 'title' | 'theme' | 'subtitle' | 'expand' | 'buttons' | 'description'> {}
const props = defineProps<Props>()
const imageContainer = ref<HTMLDivElement | null>(null)
const { width, height } = useElementBounding(imageContainer)

const placedImages = ref<{ top: number, left: number, width: number, height: number, src: string }[]>([])

onMounted(() => {
  placeImages()
})

watch(width, () => {
  placedImages.value = []
  placeImages()
})

function placeImages() {
  props.images?.forEach((image) => {
    const imageSize = getRandomSize(props.images?.length)
    let position = getRandomPosition(imageSize)

    // Try to find a non-overlapping position
    let attempts = 0
    const maxAttempts = 50

    while (isOverlapping(position, imageSize) && attempts < maxAttempts) {
      position = getRandomPosition(imageSize)
      attempts++
    }

    // Skip image if no valid position is found after max attempts
    if (isOverlapping(position, imageSize)) {
      return null // Skip this image
    }

    // Add current image to the list of placed images
    placedImages.value.push({
      src: image,
      top: position.top,
      left: position.left,
      width: imageSize,
      height: imageSize,
    })
  })
}

// Generate random position
function getRandomPosition(imageSize: number): { top: number, left: number } {
  const h = height.value || 0
  const w = width.value || 0
  const top = Math.random() * Math.max(0, (h - (imageSize * 0.5)))
  const left = Math.random() * Math.max(0, (w - (imageSize * 0.5)))
  return { top, left }
}

// Generate random size based on total number of images and available space
function getRandomSize(totalImages = 1) {
  // Dynamically set max size based on container size and number of images
  const w = width.value || 0
  const maxSize = w / totalImages
  const minSize = maxSize * 0.5
  return Math.floor(Math.random() * (maxSize - minSize) + minSize)
}

// Check for overlap with already placed images
function isOverlapping(position: { top: number, left: number }, imageSize: number) {
  const buffer = 5 // Add space between images

  return placedImages.value.some((image) => {
    const isColliding
      = !(position.left + imageSize + buffer < image.left // too far left
        || position.left > image.left + image.width + buffer // too far right
        || position.top + imageSize + buffer < image.top // too far above
        || position.top > image.top + image.height + buffer) // too far below

    return isColliding
  })
}
</script>

<template>
  <div
    class="mx-auto max-w-screen-2xl w-full px-6"
    :class="[theme]"
  >
    <HeadingText
      tag="span"
      class="text-5xl  md:text-6xl lg:text-7xl text-center mb-2 heading inline-flex justify-center 2xl:text-9xl font-bold uppercase font-display w-full"
    >
      {{ props.title }}
    </HeadingText>
    <section
      v-motion
      :visible="{ opacity: 1, y: 0 }"
      :initial="{ opacity: 0, y: 20 }"
      :class="[theme]"
      class=" w-full rounded-4xl body relative overflow-clip"
    >
      <div
        class="max-w-screen-md mx-auto pt-32 md:px-10 lg:px-12 px-8 interblock"
        :class="[images?.length ? 'pb-12' : 'pb-32']"
      >
        <div
          v-motion
          class="subtitle font-display font-bold uppercase text-2xl md:text-4xl lg:text-5xl xl:text-6xl"
          :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
          :initial="{ opacity: 0, rotate: 5, x: 20, scale: 0.5 }"
          :delay="100"
        >
          {{ props.subtitle }}
        </div>

        <PageButtons
          :buttons="expand?.buttons"
          class="pt-3 md:pt-6 lg:pt-12"
          :background="theme"
        />

        <div
          v-motion
          class="text text-lg lg:text-xl w-full paragraph"
          :visible="{ opacity: 1, rotate: 0, y: 0, x: 0, scale: 1 }"
          :initial="{ opacity: 0, rotate: 5, y: 20, x: 30, scale: 0.5 }"
          :delay="400"
        >
          <section
            class="htmldescription"
            data-allow-mismatch
            v-html="props.description"
          />
        </div>
      </div>
      <div
        ref="imageContainer"
        class="flex h-32 lg:h-64 flex-wrap relative"
      >
        <template
          v-for="({ src, ...style }, index) in placedImages"
          :key="index"
        >
          <ImageFull
            v-if="width"
            :id="id"
            loading="lazy"
            :collection-id="collectionId"
            :src="src"
            :alt="`${title}-${index + 1}`"
            class="absolute duration-200"
            :style="{ top: `${style.top}px`, left: `${style.left}px`, width: `${style.width}px`, height: `${style.height}px` }"
          />
        </template>
      </div>
    </section>
  </div>
</template>

<style scoped lang="css">
.paragraph :deep(p) {
  @apply pb-4;
}
.blue section.body {
  @apply bg-gradient-to-tl from-blue from-50% to-pink text-white;
}
.blue .heading {
  @apply text-blue;
}
.blue .subtitle {
  @apply text-yellow;
}
.blue .arrow {
  @apply text-pink;
}
.light-blue section.body {
  @apply bg-gradient-to-tl from-lightblue from-80% to-blue text-primary;
}
.light-blue .heading {
  @apply text-lightblue;
}
.light-blue .subtitle {
  @apply text-yellow;
}
.light-blue .arrow {
  @apply text-pink;
}
.pink section.body {
  @apply bg-gradient-to-tl from-pink from-60% via-pink via-60% to-lightblue text-white;
}
.pink .heading {
  @apply text-pink;
}
.pink .subtitle {
  @apply text-yellow;
}
.pink .arrow {
  @apply text-yellow;
}
</style>
